import { explicitMaybe } from '@execonline-inc/decoders';
import { paginationMetadataDecoder } from '@execonline-inc/resource';
import { alreadyTranslatedText } from '@execonline-inc/translations';
import { Decoder, array, dateISO, field, nullable, number, oneOf, string, succeed } from 'jsonous';
import { stringLiteral } from '../../../../../Decoders';
import { resourceDecoder, resourceWithMetadataDecoder } from '../../../../../Resource/Decoders';
import { InvitationStatus } from './InvitationStore/Types';
import {
  CourseRegistration,
  CourseRegistrationResource,
  Invitation,
  InvitationEmail,
  InvitationResource,
  Invitations,
  InvitationsResource,
  NotEmailed,
  RegistrationType,
  ScheduledInvitationEmail,
  SentInvitationEmail,
} from './Types';

const invitationStatusDecoder: Decoder<InvitationStatus> = oneOf<InvitationStatus>([
  stringLiteral<InvitationStatus>('active'),
  stringLiteral<InvitationStatus>('inactive'),
]);

export const registrationTypeDecoder: Decoder<RegistrationType> = oneOf<RegistrationType>([
  stringLiteral<RegistrationType>('auditor'),
  stringLiteral<RegistrationType>('student'),
]);

const courseRegistrationDecoder: Decoder<CourseRegistration> = succeed({})
  .assign('studentStatus', field('student_status', alreadyTranslatedText))
  .assign('enrolledOn', field('enrolled_on', explicitMaybe(dateISO)))
  .assign('publicName', field('public_name', alreadyTranslatedText));

export const courseRegistrationResourceDecoder: Decoder<CourseRegistrationResource> =
  resourceDecoder(courseRegistrationDecoder);

const sentInvitationEmailDecoder: Decoder<SentInvitationEmail> = succeed({})
  .assign('kind', field('kind', stringLiteral<'sent'>('sent')))
  .assign('at', field('at', dateISO))
  .assign('communicationName', field('communication_name', explicitMaybe(string)));

const scheduledInvitationEmailDecoder: Decoder<ScheduledInvitationEmail> = succeed({})
  .assign('kind', field('kind', stringLiteral<'scheduled'>('scheduled')))
  .assign('at', field('at', dateISO))
  .assign('communicationName', field('communication_name', explicitMaybe(string)));

const notEmailedDecoder: Decoder<NotEmailed> = succeed({}).assign(
  'kind',
  field('kind', stringLiteral<'not-emailed'>('not-emailed')),
);

const invitationEmailDecoder: Decoder<InvitationEmail> = oneOf<InvitationEmail>([
  sentInvitationEmailDecoder.map<InvitationEmail>((email) => email),
  scheduledInvitationEmailDecoder.map<InvitationEmail>((email) => email),
  notEmailedDecoder.map<InvitationEmail>((email) => email),
]);

const invitationDecoder: Decoder<Invitation> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('email', field('email', string))
  .assign('registrationType', field('registration_type', registrationTypeDecoder))
  .assign('totalEnrollments', field('total_enrollments', number))
  .assign('totalInvitations', field('total_invitations', number))
  .assign('invitationEmail', field('invitation_email', invitationEmailDecoder))
  .assign('invitationAcceptedDate', field('invitation_accepted_date', nullable(dateISO)))
  .assign('initials', field('initials', explicitMaybe(string)))
  .assign('invitationStatus', field('invitation_status', invitationStatusDecoder))
  .assign(
    'courseRegistration',
    field('course_registration', array(courseRegistrationResourceDecoder)),
  );

export const invitationResourceDecoder: Decoder<InvitationResource> =
  resourceDecoder(invitationDecoder);

export const invitationsDecoder: Decoder<Invitations> = succeed({})
  .assign('totalInvitations', field('total_invitations', number))
  .assign('invitations', field('invitations', array(invitationResourceDecoder)));

export const invitationsResourceDecoder: Decoder<InvitationsResource> = resourceWithMetadataDecoder(
  invitationsDecoder,
  paginationMetadataDecoder,
);
